import React, { type CSSProperties, useEffect, useState } from 'react'
import classnames from 'classnames'
import { getAriaLabel } from 'shared/lib'
import { type ITooltipProps, Tooltip } from 'shared/ui/Tooltip'
import styles from './styles.module.scss'

export type ISwitchProps = {
  size?: 'xSmall' | 'small' | 'middle' | 'large'
  disabled?: boolean
  value?: boolean
  onChange?: (status: boolean) => void
  padding?: CSSProperties['padding']
  background?: CSSProperties['background']
  className?: string
  ariaLabel?: string
  tooltipProps?: ITooltipProps
  checkedStatusText?: string
  uncheckedStatusText?: string
  isStatusTextVisible?: boolean
}

const Switch: React.FC<ISwitchProps> = ({
  size = 'small',
  disabled = false,
  value = false,
  onChange,
  padding,
  background,
  className,
  ariaLabel,
  tooltipProps,
  checkedStatusText = 'ON',
  uncheckedStatusText = 'OFF',
  isStatusTextVisible = false,
}) => {
  const [checkedLocal, setCheckedLocal] = useState(value)

  const checked = value ?? checkedLocal

  useEffect(() => {
    setCheckedLocal(value)
  }, [value])

  const onChecked = () => {
    const value = !checked

    if (disabled) return

    setCheckedLocal(value)
    onChange?.(value)
  }

  const btn = () => {
    return (
      <div
        className={classnames(
          styles.wrap,
          styles[`size_${size}`],
          {
            [styles.disabled]: disabled,
            disabled,
            [styles.checked]: checked,
          },
          className
        )}
        style={{ padding }}
        onClick={onChecked}
        aria-label={getAriaLabel(ariaLabel || 'Switch')}
      >
        <div
          className={classnames(styles.switch)}
          style={{ background }}
          aria-label={getAriaLabel('Switch', `${checked ? 'Active' : 'Disabled'}`)}
        >
          <div
            className={classnames(styles.status, styles[`size_${size}`], {
              [styles.checked]: checked,
            })}
            aria-label={getAriaLabel('Switch', 'status')}
          />
          {size === 'large' && isStatusTextVisible && (
            <span className={classnames(styles.statusText, { [styles.statusTextActive]: checked })}>
              {checked ? checkedStatusText : uncheckedStatusText}
            </span>
          )}
        </div>
      </div>
    )
  }

  if (tooltipProps) {
    return <Tooltip {...tooltipProps}>{btn()}</Tooltip>
  }

  return btn()
}

export { Switch }
